import { AfterViewChecked, Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormGroup, NgForm } from '@angular/forms';
import { Event, NavigationStart, Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Subscription } from 'rxjs';

import { DashboardService } from '../../../../dashboard/dashboard.service';
import { CheckBlock } from '../../../dmo/check-block';
import { DynamicTranslationService } from '../../../dynamic-translation/dynamic-translation.service';
import { ToggleOption } from '../../toggle-button/toggle-button.component';

import { AbstractModal } from '../AbstractModal';
import { ModalService } from '../modal.service';
import { AnalyticalComponents } from './interfaces/analytical-components.enum';
import { DashboardSettings } from './models/dashboard-settings';

@Component({
  selector: 'ls-dashboard-goal-settings-modal',
  templateUrl: 'dashboard-goal-settings-modal.component.html'
})
export class DashboardSettingsModalComponent extends AbstractModal
  implements OnInit, AfterViewChecked {
  public dashboardSettings: DashboardSettings;
  @ViewChild('settingsForm')
  public currentForm: NgForm;
  public formErrors: any = {
    'revenue-goal': '',
    'customers-goal': '',
    'receipts-goal': ''
  };
  public checkBlocks: Array<CheckBlock> = [];

  private weekdays: Array<string> = [];
  private toggleWeekdays: Array<ToggleOption> = [];
  private toggleTaxes: Array<ToggleOption> = [];
  private settingsForm: NgForm;
  private validationMessages: any = {
    'revenue-goal': {
      required: 'Revenue goal is required',
      min: 'Revenue goal must be bigger than 0',
      number: 'Revenue goal must be a number',
      pattern: 'Revenue goal can contain at maximum 11 characters'
    },
    'customers-goal': {
      required: 'Customer goal is required',
      min: 'Customer goal must be bigger than 0',
      digits: 'Customer goal must be a whole number',
      pattern: 'Customer goal can contain at maximum 11 characters'
    },
    'receipts-goal': {
      required: 'Receipt goal is required',
      min: 'Receipt goal must be bigger than 0',
      digits: 'Receipt goal must be a whole number',
      pattern: 'Receipt goal can contain at maximum 11 characters'
    }
  };

  constructor(
    private modalService: ModalService,
    private dashboardService: DashboardService,
    private router: Router,
    private translation: DynamicTranslationService
  ) {
    super();
    this.router.events.forEach((event: Event) => {
      if (event instanceof NavigationStart) {
        this.close();
      }
    });
  }

  public ngAfterViewChecked(): void {
    this.formChanged();
  }

  public ngOnInit(): void {
    this.weekdays = moment.weekdaysMin();
    const subscription: Subscription = this.dashboardService.dashboardSettings$.subscribe(
      (settings: DashboardSettings) => {
        this.dashboardSettings = _.cloneDeep(settings);
        this.checkBlocks = this.getCheckBlocks(settings);
        this.setToggleWeekdays();
        this.setToggleTaxes();
        this.updateSettings();
        subscription.unsubscribe();
      }
    );
    this.dashboardService.getDashboardSettings();
  }

  /**
   * Check if day is a closing day
   * @param dayOfWeek
   * @returns {boolean}
   */
  public isClosingDay(dayOfWeek: number): boolean {
    return (
      this.dashboardSettings.closingDays &&
      this.dashboardSettings.closingDays.indexOf(dayOfWeek) !== -1
    );
  }

  /**
   * event handler save button
   */
  public saveSettings(isValid: boolean): void {
    if (isValid) {
      this.updateSettings();
      const subscription: Subscription = this.dashboardService.dashboardSettingChanges$.subscribe(
        (dashboardGoalSetting: DashboardSettings) => {
          this.modalService.close(dashboardGoalSetting);
          subscription.unsubscribe();
        }
      );
      this.dashboardService.saveSettings(this.dashboardSettings);
    }
  }

  /**
   * event handler cancel button
   */
  public close(): void {
    this.modalService.close();
  }

  public updateShowHideComponents(checkBlocks: Array<CheckBlock>): void {
    this.dashboardSettings.hiddenComponents = checkBlocks
      .filter((checkBlock: CheckBlock) => !checkBlock.checked)
      .map((checkBlock: CheckBlock) => <AnalyticalComponents>checkBlock.name);
  }

  public isTargetDisabled(target: string): boolean {
    if (this.checkBlocks.length > 0) {
      return this.checkBlocks.some(
        (checkBlock: CheckBlock) =>
          checkBlock.name === target && !checkBlock.checked
      );
    }
    return true;
  }

  private updateSettings(): void {
    this.dashboardSettings.taxIncl = this.toggleTaxes[0].checked;
    this.dashboardSettings.closingDays = [];
    for (let i = 0; i < this.toggleWeekdays.length; i++) {
      if (this.toggleWeekdays[i].checked) {
        this.dashboardSettings.closingDays.push(i);
      }
    }
  }

  private setToggleWeekdays(): void {
    // Outputs according to moment day numbering, monday till sunday
    for (let i = 0; i < this.weekdays.length; i++) {
      this.toggleWeekdays.push({
        label: this.weekdays[i],
        checked: this.isClosingDay(i)
      });
    }
  }

  private setToggleTaxes(): void {
    this.toggleTaxes.push({
      label: 'Tax inclusive',
      checked: this.dashboardSettings.taxIncl
    });
    this.toggleTaxes.push({
      label: 'Tax exclusive',
      checked: !this.dashboardSettings.taxIncl
    });
  }

  private formChanged(): void {
    if (this.currentForm === this.settingsForm) {
      return;
    }
    this.settingsForm = this.currentForm;
    if (this.settingsForm) {
      this.settingsForm.valueChanges.subscribe(() => this.onFormChanged());
    }
  }

  private onFormChanged(): void {
    if (!this.settingsForm) {
      return;
    }
    const form: FormGroup = this.settingsForm.form;
    for (const field in this.formErrors) {
      if (this.formErrors.hasOwnProperty(field)) {
        this.formErrors[field] = '';
        const control: AbstractControl = form.get(field);
        if (control && control.dirty && !control.valid) {
          const messages: any = this.validationMessages[field];
          for (const key in messages) {
            if (control.errors[key]) {
              this.formErrors[field] = messages[key];
              break;
            }
          }
        }
      }
    }
  }

  private getCheckBlocks(
    dashboardSettings: DashboardSettings
  ): Array<CheckBlock> {
    return CheckBlock.getDefaultBlockConfiguration().map(
      (checkBlock: CheckBlock) => {
        checkBlock.tooltip = this.translation.translate(checkBlock.tooltip);
        checkBlock.checked =
          dashboardSettings.hiddenComponents.indexOf(<AnalyticalComponents>(
            checkBlock.name
          )) === -1;
        return checkBlock;
      }
    );
  }
}
